import React, { createContext, useContext, useMemo } from 'react'
import { enviroment } from '../enviroment'

import { OrganizationRepository } from '../data/repositories/OrganizationRepository'
import { DesignRepository } from '../data/repositories/DesignRepository'
import { LogoRepository } from '../data/repositories/LogoRepository'
import { ManufacturerRepository } from '../data/repositories/ManufacturerRepository'
import { OrderRepository } from '../data/repositories/OrderRepository'
import { StyleRepository } from '../data/repositories/StyleRepository'
import { UserRepository } from '../data/repositories/UserRepository'
import { CountryRepository } from '../data/repositories/CountryRepository'
import { StatisticsRepository } from '../data/repositories/StatisticsRepository'
import { CbsConfigRepository } from '../data/repositories/CbsConfigRepository'
import { ApiKeyRepository } from '../data/repositories/ApiKeyRepository'
import { PlaceOrderQueueRepository } from '../data/repositories/PlaceOrderQueueRepository'
import { TransferMethodConfigRepository } from '../data/repositories/TransferMethodConfigRepository'
import { StockOrdersConfigRepository } from '../data/repositories/StockOrdersConfigRepository'
import { DesignFolderRepository } from '../data/repositories/DesignFolderRepository'
import { ContentRepository } from '../data/repositories/ContentRepository'
import { PrepaymentsRepository } from '../data/repositories/PrepaymentsRepository'
import { ImportFailuresRepository } from '../data/repositories/ImportFailuresRepository'
import { SystemHealthRepository } from '../data/repositories/SystemHealthRepository'
import { StripePayoutsRepository } from '../data/repositories/StripePayoutsRepository'
import { NavPriceFallbackRepository } from '../data/repositories/NavPriceFallbackRepository'
import { MailTestRepository } from '../data/repositories/MailTestRepository'
import { ApprovalMailRepository } from '../data/repositories/ApprovalMailRepository'
import { StockStatusReportRepository } from '../data/repositories/StockStatusReportRepository'

interface Repositories {
  styleRepository: StyleRepository
  designRepository: DesignRepository
  logoRepository: LogoRepository
  organizationRepository: OrganizationRepository
  orderRepository: OrderRepository
  manufacturerRepository: ManufacturerRepository
  userRepository: UserRepository
  countryRepository: CountryRepository
  statisticsRepository: StatisticsRepository
  cbsConfigRepository: CbsConfigRepository
  apiKeyRepository: ApiKeyRepository
  placeOrderQueueRepository: PlaceOrderQueueRepository
  transferMethodConfigRepository: TransferMethodConfigRepository
  stockOrdersConfigRepository: StockOrdersConfigRepository
  designFolderRepository: DesignFolderRepository
  contentRepository: ContentRepository
  prepaymentsRepository: PrepaymentsRepository
  importFailuresRepository: ImportFailuresRepository
  systemHealthRepository: SystemHealthRepository
  stripePayoutsRepository: StripePayoutsRepository
  navPriceFallbackRepository: NavPriceFallbackRepository
  mailTestRepository: MailTestRepository
  approvalMailRepository: ApprovalMailRepository
  stockStatusReportRepository: StockStatusReportRepository
}

const RepositoriesContext = createContext<Repositories | undefined>(undefined)

interface RepositoriesProviderProps {
  repositories: Repositories
  children: React.ReactNode
}

function RepositoriesProvider({
  repositories,
  children
}: RepositoriesProviderProps): React.ReactElement {
  // Admin perf FE-2 (flag adminPerfNavMemoEnabled): one value object per
  // `repositories` prop identity, so the ~90 useRepositoriesContext consumers
  // re-render only when a repository really changed. Flag off = the literal
  // object per render as before (useMemo is called either way: stable hooks).
  const memoValue = useMemo(() => ({ ...repositories }), [repositories])
  const value = enviroment.adminPerfNavMemoEnabled
    ? memoValue
    : {
        styleRepository: repositories.styleRepository,
        designRepository: repositories.designRepository,
        logoRepository: repositories.logoRepository,
        organizationRepository: repositories.organizationRepository,
        orderRepository: repositories.orderRepository,
        manufacturerRepository: repositories.manufacturerRepository,
        userRepository: repositories.userRepository,
        countryRepository: repositories.countryRepository,
        statisticsRepository: repositories.statisticsRepository,
        cbsConfigRepository: repositories.cbsConfigRepository,
        apiKeyRepository: repositories.apiKeyRepository,
        placeOrderQueueRepository: repositories.placeOrderQueueRepository,
        transferMethodConfigRepository: repositories.transferMethodConfigRepository,
        stockOrdersConfigRepository: repositories.stockOrdersConfigRepository,
        designFolderRepository: repositories.designFolderRepository,
        contentRepository: repositories.contentRepository,
        prepaymentsRepository: repositories.prepaymentsRepository,
        importFailuresRepository: repositories.importFailuresRepository,
        systemHealthRepository: repositories.systemHealthRepository,
        stripePayoutsRepository: repositories.stripePayoutsRepository,
        navPriceFallbackRepository: repositories.navPriceFallbackRepository,
        mailTestRepository: repositories.mailTestRepository,
        approvalMailRepository: repositories.approvalMailRepository,
        stockStatusReportRepository: repositories.stockStatusReportRepository
      }
  return (
    <RepositoriesContext.Provider value={value}>
      {children}
    </RepositoriesContext.Provider>
  )
}

function useRepositoriesContext(): Repositories {
  const context = useContext(RepositoriesContext)
  if (context === undefined) {
    throw new Error('useRepositoriesContext must be used within a RepositoriesProvider')
  }
  return context
}

export { RepositoriesProvider, useRepositoriesContext }
